import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { isDevMock } from '../dev/isDevMock'
import { MOCK_AUTH_USER } from '../dev/mockAuth'
import { fetchAuthMe, markAuthenticated } from '../lib/apiFetch'

interface AuthUser {
  id: string
  name: string
  email: string | null
  picture: string | null
  isAdmin?: boolean
  // True when an admin has flagged this account for abuse. A flagged user is locked out of the app by
  // a non-dismissable overlay (see AbuseOverlay), read via useIsFlaggedForAbuse().
  flaggedForAbuse?: boolean
}

interface AuthState {
  user: AuthUser | null
  loading: boolean
  checked: boolean
  inFlight: boolean
  authError: boolean
  // Whether an admin is currently viewing the app in admin mode. Only meaningful for real admins
  // and toggleable via the "Admin Features" item in the profile menu (see UserMenu / useAdminMode);
  // an admin can flip it off to preview the app as a normal user would see it. Persisted so the
  // preference survives reloads.
  adminMode: boolean
}

const ADMIN_MODE_KEY = 'loopqa:adminMode'

function readAdminMode(): boolean {
  try {
    // Default on: admins see admin UI unless they've explicitly opted into the user preview.
    return localStorage.getItem(ADMIN_MODE_KEY) !== '0'
  } catch {
    return true
  }
}

const initialState: AuthState = {
  user: null,
  loading: true,
  checked: false,
  inFlight: false,
  authError: false,
  adminMode: readAdminMode(),
}

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async () => {
    if (isDevMock()) return MOCK_AUTH_USER as AuthUser
    const response = await fetchAuthMe()
    if (!response.ok) {
      throw new Error('AUTH_PROVIDER_UNAVAILABLE')
    }
    const data = (await response.json()) as { user: AuthUser | null }
    return data.user
  },
  {
    condition: (_, { getState }) => {
      const { auth } = getState() as { auth: AuthState }
      return !auth.checked && !auth.inFlight
    },
  },
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    devMockSignIn(state) {
      state.user = MOCK_AUTH_USER as AuthUser
      state.loading = false
      state.checked = true
    },
    devMockSignOut(state) {
      state.user = null
      state.loading = false
      state.checked = true
    },
    setAdminMode(state, action: PayloadAction<boolean>) {
      state.adminMode = action.payload
      try {
        localStorage.setItem(ADMIN_MODE_KEY, action.payload ? '1' : '0')
      } catch {
        /* ignore */
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.inFlight = true
        if (!state.checked) {
          state.loading = true
        }
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload
        state.loading = false
        state.checked = true
        state.inFlight = false
        state.authError = false
        if (action.payload) markAuthenticated()
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.user = null
        state.loading = false
        state.checked = true
        state.inFlight = false
        state.authError = action.error.message === 'AUTH_PROVIDER_UNAVAILABLE'
      })
  },
})

export const { devMockSignIn, devMockSignOut, setAdminMode } = authSlice.actions
export default authSlice.reducer
